/**
 * @file src/offline/offlineAvailability.js
 * @description "Are this track's bytes already here?", answered without awaiting anything.
 *
 * One caller needs this and it is not a UI one. The playback strategy picks buffered-versus-streamed
 * while a voice is being built (`AudioEngineAdapter.#resolveDecodeStrategy`), a decision that is
 * synchronous all the way down, and it has to come out buffered for a downloaded track — a streaming
 * media element issues range requests, and a whole-response cache entry cannot answer a 206 until
 * there is a service worker to slice one.
 *
 * So the pinned key set is kept in memory and asked here. Being a snapshot is the honest trade: it
 * is refreshed on every library change, and both ways of being wrong are cheap — a stale "no" is a
 * streamed load that could have been buffered, a stale "yes" is a buffered load that falls back to
 * the network. Neither is a failure, which is why a snapshot is allowed to stand in for the store.
 *
 * The identity is computed the same way playback computes it, from the same builders, so this can
 * never answer about a key playback would not look for.
 */
import { isKeyPinnedSync } from './offlineLibrary.js';
import { isMultiplayerBoot } from '../utils/urlParams.js';
import { OFFLINE_QUALITY, audioVariantForQuality } from './offlineConfig.js';
import { toCacheRequestUrl, trackAudioKey } from './offlineKeys.js';

/**
 * Which variant of this track a download is holding, or null when none is.
 *
 * A download can now be kept at either quality, per collection, so the question is no longer "is the
 * compressed copy here" but "which copy is here". Lossless is asked about first: when a person holds
 * both (two collections, two qualities — each pins its own), the better one is the one to play, and
 * it costs nothing to prefer it since both are already on disk.
 *
 * PINNED, not merely cached. A track whose lossless happens to sit in the play cache is not a
 * downloaded track: those bytes are free to be evicted mid-session, and answering yes on their
 * strength would force the buffered sink onto something that can vanish under it. A pin is exactly
 * what eviction refuses to take, which is why the pinned ledger is the thing consulted.
 *
 * @param {object|null} trackData a normalized track release (`normalizeTrackRelease`)
 * @returns {{ quality: string, variant: string, assetFormat: string }|null}
 */
export function downloadedTrackVariant(trackData) {
  // Never in a ROOM. A room is people listening together, and which variant each of them happens to
  // have on disk is nobody else's business — a download deciding there would put participants on
  // different files. Answered here rather than at each call site so the variant chosen for playback
  // and the buffered-versus-streamed decision can never disagree, which is its own class of bug.
  //
  // `isMultiplayerBoot` and not the presence of a grant: a room boot carries `?room=`, and the grant
  // is minted later — a voice built before it exists would read "not a room" and keep its own copy
  // for the rest of that voice's life. This is the same boot-frozen answer playback policy uses.
  // Guarded: the boot fact is read off the address, and this module is also imported where there is
  // no document (the worker-side and node test paths). No window is not a room.
  if (typeof window !== 'undefined' && isMultiplayerBoot()) return null;

  const trackId = trackData?.trackId ?? null;
  const version = trackData?.version ?? null;
  if (!trackId || version === null || version === undefined) return null;

  for (const quality of [OFFLINE_QUALITY.LOSSLESS, OFFLINE_QUALITY.COMPRESSED]) {
    const { variant, assetFormat } = audioVariantForQuality(quality);
    const key = trackAudioKey({ trackId, version, variant, assetFormat });
    if (isKeyPinnedSync(toCacheRequestUrl(key))) return { quality, variant, assetFormat };
  }
  return null;
}

/**
 * Whether a normalized track release's audio is stored locally, at either quality.
 *
 * @param {object|null} trackData a normalized track release (`normalizeTrackRelease`)
 * @returns {boolean}
 */
export function isTrackDownloadedSync(trackData) {
  return downloadedTrackVariant(trackData) !== null;
}
